import { create } from "domain";

import { Add } from "@mui/icons-material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Box,
  TableContainer,
  CircularProgress,
  Backdrop,
  Typography,
  Tooltip,
} from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import GenericTable from "0.Mine/0.Components/Reusable/GenericTable";
import { useHandleWorkFlow } from "0.Mine/1.Hooks/CRM/workflow";
import { CreateWorkFlowWithAI } from "0.Mine/1.Hooks/utils/ai";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { WorkFlow, WorkflowResponse } from "0.Mine/3.Interfaces/workFlow";
import { SafeArgonButton } from "0.Mine/4.Argon/ArgonComponents";

import WorkflowSettingsModal from "./GenerateWithAIModal";
import { WorkflowModal } from "./SequenceEditor";

const SequenceInfo = () => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [openEditModal, setOpenEditModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const { session } = useBusinessContext();
  const { createFlow } = CreateWorkFlowWithAI();
  const { getallWorkFlows, getOneWorkFlow, submitNewWorkflow, deleteWorkFlow, toggleWorkflow } =
    useHandleWorkFlow();
  const [workflows, setWorkflows] = React.useState<WorkflowResponse[]>([]);
  const [selectedWorkflow, setSelectedWorkFlow] = React.useState<WorkFlow>();

  useEffect(() => {
    const fetchData = async () => {
      if (session && session.selectedBusiness) {
        const response = await getallWorkFlows(session.selectedBusiness.id);
        if (response) {
          setWorkflows(response);
        } else {
          console.error("Error fetching workflows");
          setWorkflows([]);
        }
      }
    };

    fetchData();
  }, [session]);

  if (!session || !session.selectedBusiness) {
    return <p>Select a business</p>;
  }
  const handleClose = () => {
    setOpen(false);
  };

  const closeEditModal = () => {
    setOpenEditModal(false);
  };

  const onSubmit = async (goal: string, tone: string) => {
    console.log("Workflow settings submitted", goal, tone);
    setOpen(false);
    // Show loading screen
    setIsLoading(true);
    try {
      // Call the function to create a workflow with AI here
      const response = await createFlow(session.selectedBusiness, tone, goal);
      console.log("AI Workflow response", response);
      if (response) {
        const workflow = await submitNewWorkflow(session.selectedBusiness.id, response);
        console.log("Workflow created", workflow);
        // Add the new workflow to the list
        if (workflow) {
          setWorkflows((prevWorkflows) => [...prevWorkflows, workflow]);
          const workflowData = await getOneWorkFlow(session.selectedBusiness.id, workflow.id);
          setSelectedWorkFlow(workflowData);
          setOpenEditModal(true);
        }
      }
    } catch (error) {
      console.error("Error creating workflow:", error);
    } finally {
      // Hide loading screen
      setIsLoading(false);
    }
  };

  const handleRowClick = async (row: number) => {
    const workflow = await getOneWorkFlow(session.selectedBusiness.id, row);
    setSelectedWorkFlow(workflow);
    setOpenEditModal(true);
  };

  const handleSave = async (workflow: WorkFlow) => {
    try {
      const updatedWorkflow = await submitNewWorkflow(session.selectedBusiness.id, workflow);

      setSelectedWorkFlow(workflow);
      setWorkflows((prevWorkflows) =>
        prevWorkflows.map((w) => (w.template_name === workflow.templateName ? updatedWorkflow : w))
      );

      setOpenEditModal(false);
    } catch (error) {
      console.error("Error updating workflow:", error);
    }
  };
  return (
    <div>
      <TableContainer>
        {/* Header actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 2,
            position: "relative",
            zIndex: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Titolo + Tooltip */}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ color: "#37376D" }}>
              Crea dei Workflow e utilizzali attraverso il generatore di regole
            </Typography>
            <Tooltip
              title={
                <>
                  I workflow ti permettono di automatizzare le sequenze di azioni. <br />
                  Una volta creati, possono essere attivati tramite regole personalizzate{" "}
                  <b>nella sezione Gestione Leads</b>.
                </>
              }
              arrow
              placement="right"
            >
              <HelpOutlineIcon sx={{ fontSize: 20, color: "#37376D", cursor: "pointer" }} />
            </Tooltip>
          </Box>

          {/* Bottoni */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <SafeArgonButton
              variant="gradient"
              color="secondary"
              size="small"
              onClick={() => setOpen(true)}
              startIcon={<Add />}
            >
              Crea con Intelligenza Artificiale
            </SafeArgonButton>
            <SafeArgonButton
              variant="gradient"
              color="primary"
              size="small"
              onClick={() => navigate("/crm/template", { replace: true })}
              startIcon={<Add />}
            >
              Crea Workflow
            </SafeArgonButton>
          </Box>
        </Box>

        <GenericTable
          columns={["template_name", "steps_count"]}
          columnLabels={{
            template_name: "Nome",
            steps_count: "Passi",
          }}
          data={Array.isArray(workflows) ? workflows : []}
          noDataMessage="Nessuna sequenza. Aggiungine una oggi!"
          enableToggleColumn={true}
          toggleField="is_active"
          onToggleChange={async (rowId, newValue) => {
            const workflowId = typeof rowId === "string" ? parseInt(rowId, 10) : rowId;

            setWorkflows((prevWorkflows) =>
              prevWorkflows.map((workflow) =>
                workflow.id === workflowId ? { ...workflow, is_active: newValue ? 1 : 0 } : workflow
              )
            );

            try {
              await toggleWorkflow(session.selectedBusiness.id, workflowId);
            } catch (error) {
              console.error("Error toggling workflow:", error);
              setWorkflows((prevWorkflows) =>
                prevWorkflows.map((workflow) =>
                  workflow.id === workflowId
                    ? { ...workflow, is_active: !newValue ? 1 : 0 }
                    : workflow
                )
              );
            }
          }}
          rowActions={[
            {
              name: "Modifica",
              onClick: (row) => {
                handleRowClick(row.id);
              },
            },
            {
              name: "Elimina",
              onClick: async (row) => {
                await deleteWorkFlow(session.selectedBusiness.id, row.id);
                setWorkflows((prevWorkflows) =>
                  prevWorkflows.filter((workflow) => workflow.id !== row.id)
                );
              },
            },
          ]}
        />
      </TableContainer>

      {/* Loading backdrop */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 2,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">Generazione del workflow attraverso AI...</Typography>
      </Backdrop>

      <WorkflowSettingsModal open={open} onClose={handleClose} onSubmit={onSubmit} />
      <WorkflowModal
        open={openEditModal}
        onClose={closeEditModal}
        workflowData={selectedWorkflow}
        onSave={handleSave}
      />
    </div>
  );
};

export default SequenceInfo;
